import getConfigValue from '@/config';
import usePermissions from '@/hooks/usePermissions';
import { BaseRejectionReasons } from '@/types/BasePackageTypes';
import { Alert, Box, Button, Icon, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  CLAIMABLE_QUEUE_STATUSES,
  DomesticContentRejectionReasons,
  PackageRejectionInfo,
  QueueStatus,
  queueStatusMap,
  QueueType,
  QueueTypeLabel,
  UserPermissions,
} from 'types';

import { useGetQueueByAccountIdAndTypeQuery, useUpdateQueueMutation } from '../../services/queues';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { HiddenLightReachInfoForEpcUser } from '../auth/HiddenLightReachInfoToEpcUser';
import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';
import DateTimeDisplay from '../DateTime';
import { QueueFlagsContext } from '../QueueFlags/QueueFlagsContext';
import { FlagCount } from '../Queues/CompleteQueueReview/FlagCount';
import { RemoveClaimModal } from '../Queues/RemoveClaimModal';
import { RejectionReasonsList } from '../Reject/RejectionReasonsList';

import './QueueBanner.css';

const getMostRecentObj = (array: any[]) => {
  return array?.reduce((prev: any, current: any) => {
    return prev?.date > current?.date ? prev : current;
  });
};

export const QueueBanner = ({
  queueType,
  setIsNotesOpen,
  isFinCoUser,
  hideFlagCount = false,
  is8090Project = false,
  dueDate,
}: {
  queueType: QueueType;
  setIsNotesOpen: (isNotesOpen: boolean) => void;
  isFinCoUser: boolean;
  hideFlagCount?: boolean;
  is8090Project?: boolean;
  dueDate?: string;
}) => {
  const { flagDrivenPackageReview } = useFlags();
  const { id = '' } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { getAllFlags } = useContext(QueueFlagsContext);
  const userPermissions = usePermissions();
  const isLightReachQCTech = userPermissions.includes(UserPermissions.lightReachQCTech);
  const eightyNinetyBaseUrl = getConfigValue('VITE_APP_8090_APP_BASE_URL');

  const flags = useMemo(() => {
    if (getAllFlags) {
      return getAllFlags(false);
    } else {
      return [];
    }
  }, [getAllFlags]);
  const flagCount = flags.filter((flag) =>
    flagDrivenPackageReview ? flag?.flagType || flag?.dataKey : !flag?.flagType,
  ).length;

  const { data: queue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery(
    {
      accountId: id,
      type: queueType,
    },
    { skip: !isLightReachQCTech || !id },
  );
  const [modalData, setModalData] = useState<Record<string, string> | undefined>();
  const { isOpen: isModalOpen, handleClose: handleModalClose, handleOpen: openModal } = useOpenClose();
  const [updateQueue] = useUpdateQueueMutation();
  const allowsAction = useIsOrgReadOnly();

  const removeClaim = useCallback(() => {
    setModalData({ assigneeName: queue.assignee.name, id: queue.id });
    openModal();
  }, [openModal, queue]);

  const claimQueue = useCallback(async () => {
    try {
      await updateQueue({
        data: { assigneeId: user?.sub },
        id: queue.id,
      }).unwrap();
      toast.success('Queue claimed');
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error claiming the queue.');
    }
  }, [updateQueue, user, queue]);

  if (!isLightReachQCTech) return null;

  const mostRecentHoldOrRejectionObj =
    queue?.status === QueueStatus.rejected
      ? getMostRecentObj(queue?.rejectionReasons)
      : queue?.status === QueueStatus.onHold
        ? getMostRecentObj(queue?.holdReasons)
        : undefined;
  const approvalNote =
    [QueueStatus.completeHoldForApproval, QueueStatus.approved, QueueStatus.conditionallyApproved].includes(
      queue?.status,
    ) && queue?.approvalNotes
      ? getMostRecentObj(queue.approvalNotes)
      : undefined;
  const mostRecentRejectionInfo =
    queue?.status === QueueStatus.rejected && queue?.rejectionReasons
      ? getMostRecentObj(queue?.rejectionReasons).rejectionInfo
      : {};
  const rejectedInfoArray: PackageRejectionInfo[] = mostRecentRejectionInfo
    ? Object.values(mostRecentRejectionInfo)
    : [];

  const mappedOnHoldOrRejectionReasons = mostRecentHoldOrRejectionObj?.reasons?.map(
    (reason: keyof typeof BaseRejectionReasons) => {
      const labelReason =
        BaseRejectionReasons[reason] ||
        DomesticContentRejectionReasons[reason as keyof typeof DomesticContentRejectionReasons] ||
        reason;
      return labelReason;
    },
  );
  const isQueueClaimable = CLAIMABLE_QUEUE_STATUSES.has(queue?.status);
  return (
    <>
      <RemoveClaimModal isModalOpen={isModalOpen} closeModal={handleModalClose} data={modalData} />
      {!isQueueLoading && queue ? (
        <Box
          shadow="sm"
          radius="md"
          direction="row"
          alignItems="center"
          width="100"
          background="primary"
          overflow="hidden"
        >
          <Box borderColor="separator" borderWidth="0 xs 0 0" justifyContent="center" width="2xl" padding="lg">
            <Box childGap="xs" alignItems="center" className={queue.status}>
              <Box alignItems="center" childGap="xs">
                {queue.status === QueueStatus.onHold && <Icon name="pause" size="lg" color="warning-500" />}
                {queue.status === QueueStatus.rejected && <Icon name="remove" size="lg" color="danger" />}
                {queue.status === QueueStatus.completeHoldForApproval && (
                  <Icon name="check" size="lg" color="success" />
                )}
                {queue.status === QueueStatus.approved && <Icon name="check" size="lg" color="success" />}
                {queue.status === QueueStatus.conditionallyApproved && (
                  <Icon name="check" size="lg" color="success" />
                )}
                <Box textAlign="center" fontSize="sm" fontWeight="medium">
                  {queueStatusMap[queue.status as QueueStatus]}
                </Box>
              </Box>
              <Box>
                <DateTimeDisplay
                  value={queue.updatedAt}
                  showDuration
                  fontSize="xs"
                  dateFormat="DD h:mm:ss a"
                  inline
                />
              </Box>
            </Box>
          </Box>
          <Box direction={{ tablet: 'row', base: 'column' }} gap="lg" width="100" padding="md lg">
            {!hideFlagCount && flagCount > 0 ? <FlagCount count={flagCount} /> : null}
            {approvalNote ? (
              <Box justifyContent="center" gap="xs">
                <Box direction="row" gap="xs">
                  {approvalNote?.note && <Box style={{ fontStyle: 'italic' }}>{approvalNote.note}</Box>}
                </Box>
                <Box fontSize="xs" direction="row" gap="2xs">
                  <DateTimeDisplay
                    value={approvalNote?.date}
                    showDuration={false}
                    fontSize="xs"
                    dateFormat="MMM d, yyyy h:mm a"
                  />
                  <RequirePalmettoFinanceUser>by {approvalNote?.user.name}</RequirePalmettoFinanceUser>
                </Box>
              </Box>
            ) : null}
            {[QueueStatus.rejected, QueueStatus.onHold].includes(queue.status) && !!mostRecentHoldOrRejectionObj ? (
              <Box justifyContent="center" gap="xs" flex="auto">
                {mappedOnHoldOrRejectionReasons.length > 0 ? (
                  <>
                    <Box direction="row" gap="xs">
                      <Box fontWeight="medium">{mappedOnHoldOrRejectionReasons.join(', ')}</Box>
                      {mostRecentHoldOrRejectionObj?.note && (
                        <Box style={{ fontStyle: 'italic' }}>{`- "${mostRecentHoldOrRejectionObj.note}"`}</Box>
                      )}
                    </Box>
                    <Box>
                      {rejectedInfoArray && rejectedInfoArray?.length > 0 && (
                        <Box>
                          Reasons:{' '}
                          {rejectedInfoArray.map((rejectedReasons: PackageRejectionInfo, index) => (
                            <RejectionReasonsList rejectionInfo={rejectedReasons} key={index} />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </>
                ) : null}
                <Box fontSize="xs" direction="row" gap="2xs">
                  <DateTimeDisplay
                    value={mostRecentHoldOrRejectionObj?.date}
                    showDuration={false}
                    fontSize="xs"
                    dateFormat="MMM d, yyyy h:mm a"
                  />
                  <HiddenLightReachInfoForEpcUser fallbackChildren={<Box>by LightReach Support</Box>}>
                    <Box>by {mostRecentHoldOrRejectionObj?.user?.name}</Box>
                  </HiddenLightReachInfoForEpcUser>
                </Box>
              </Box>
            ) : null}

            {isFinCoUser ? (
              <>
                {isQueueClaimable && queue.assignee && queue.assignee.id ? (
                  <Box direction="row" gap="2xs" alignItems="center" fontSize="xs">
                    <Icon name="c-user" />
                    <Box>Claimed by {queue.assignee.name}</Box>
                    <Box>
                      <Button
                        variant="tertiary"
                        tone="neutral"
                        aria-label="remove"
                        iconSuffix="c-remove"
                        size="xs"
                        onClick={removeClaim}
                      >
                        remove
                      </Button>
                    </Box>
                  </Box>
                ) : isQueueClaimable ? (
                  <Button variant="primary" tone="primary" onClick={claimQueue} disabled={!allowsAction}>
                    Claim For Review
                  </Button>
                ) : null}
              </>
            ) : null}
            {dueDate && (
              <Box direction="row" alignItems="center" gap="sm">
                <span>Due Date:</span>
                <DateTimeDisplay value={dueDate} showDuration={false} fontSize="sm" dateFormat="MMM d, yyyy" />
              </Box>
            )}
            {isFinCoUser && is8090Project ? (
              <>
                <Button
                  as="a"
                  variant="primary"
                  tone="neutral"
                  href={`${eightyNinetyBaseUrl}/search?packetId=${id}`}
                  target="_blank"
                >
                  Open in 8090
                </Button>
              </>
            ) : null}
            <RequirePalmettoFinanceUser>
              <Button
                style={{ marginLeft: 'auto' }}
                variant="primary"
                tone="neutral"
                onClick={() => setIsNotesOpen(true)}
              >
                Notes & History
              </Button>
            </RequirePalmettoFinanceUser>
          </Box>
        </Box>
      ) : null}
      {rejectedInfoArray.length > 0 && (
        <Alert
          variant="danger"
          title={`${QueueTypeLabel[queueType as keyof typeof QueueTypeLabel]} Rejected`}
          message={
            <Box>
              Reasons:{' '}
              {rejectedInfoArray.map((rejectedReasons: PackageRejectionInfo, key: number) => (
                <RejectionReasonsList key={key} rejectionInfo={rejectedReasons} />
              ))}
            </Box>
          }
          hasIcon
        />
      )}
    </>
  );
};
